import React, { useState, useEffect, useMemo } from 'react';
import { formatRupiahFull } from '../data/financialData';
import { useData } from '../DataContext';
import { parseHybridSync } from '../utils/syncParser';


interface DatabasePageProps {
  activeEntity: string;
}

interface Adjustment {
  no_journal: string;
  tanggal: string;
  no_acc: number;
  name_acc: string;
  keterangan: string;
  area: string;
  debit: number;
  credit: number;
  amount: number;
}

export const DatabasePage: React.FC<DatabasePageProps> = ({ activeEntity }) => {
  const { branches } = useData();
  // Navigation tabs: 'list', 'import', 'sync'
  const [activeTab, setActiveTab] = useState<'list' | 'import' | 'sync'>('list');

  // Autocomplete data loaded dynamically from active entity's gl_transactions API
  const [uniqueAccounts, setUniqueAccounts] = useState<{ code: string; name: string }[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);

  // 1. Adjustment Form State
  const [formDate, setFormDate] = useState(new Date().toISOString().substring(0, 10));
  const [formAccountCode, setFormAccountCode] = useState('');
  const [formAccountName, setFormAccountName] = useState('');
  const [formDesc, setFormDesc] = useState('Penyesuaian Jurnal Buku Besar');
  const [formBranch, setFormBranch] = useState('HO');
  const [formDebit, setFormDebit] = useState('0');
  const [formCredit, setFormCredit] = useState('0');
  
  const [showFormSuggestions, setShowFormSuggestions] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [formSuccessMessage, setFormSuccessMessage] = useState<string | null>(null);
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);

  // 2. Adjustments List State
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [listErrorMessage, setListErrorMessage] = useState<string | null>(null);

  // 3. Import State
  const [pasteText, setPasteText] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // 4. Google Sheets Sync State
  const [spreadsheetId, setSpreadsheetId] = useState('1jVEh7BvrFYEIKwBjv2UskrpkMWMs5pTWVHcgk7ogM_s');
  const [syncLogs, setSyncLogs] = useState<string[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [dbStatus, setDbStatus] = useState<any>(null);
  const [syncProgress, setSyncProgress] = useState<{ pct: number; step: string; detail: string }>({ pct: 0, step: '', detail: '' });
  const [syncResult, setSyncResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [isApiOffline, setIsApiOffline] = useState(false);
  const [isLocalApiOffline, setIsLocalApiOffline] = useState(true);

  // --- Dynamic Autocomplete & Account resolution ---
  useEffect(() => {
    let mounted = true;
    setIsDataLoading(true);

    const loadAccounts = async () => {
      try {
        const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
        const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

        const qParams = new URLSearchParams({
          entity: activeEntity,
          distinct: 'accounts'
        });

        const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${API_KEY}` }
        });
        if (!res.ok) throw new Error('Gagal memuat daftar akun autocomplete');
        const data = await res.json();
        if (mounted) {
          setUniqueAccounts(data);
          setIsDataLoading(false);
        }
      } catch (err) {
        console.error("Failed to load active GL for autocomplete:", err);
        if (mounted) {
          setIsDataLoading(false);
        }
      }
    };

    loadAccounts();
    return () => { mounted = false; };
  }, [activeEntity]);

  // Suggestions based on active user typing
  const accountSuggestions = useMemo(() => {
    if (!formAccountCode.trim() && !formAccountName.trim()) return [];
    const codeQuery = formAccountCode.toLowerCase();
    const nameQuery = formAccountName.toLowerCase();
    
    return uniqueAccounts
      .filter((acc) => {
        if (codeQuery) return acc.code.toLowerCase().includes(codeQuery);
        if (nameQuery) return acc.name.toLowerCase().includes(nameQuery);
        return false;
      })
      .slice(0, 8);
  }, [uniqueAccounts, formAccountCode, formAccountName]);

  // Handle autocomplete selection
  const handleSelectAccount = (code: string, name: string) => {
    setFormAccountCode(code);
    setFormAccountName(name);
    setShowFormSuggestions(false);
  };

  // --- Double-entry balance status ---
  const debitNum = parseFloat(formDebit) || 0;
  const creditNum = parseFloat(formCredit) || 0;
  const isBalanced = debitNum === creditNum && debitNum > 0;
  const balanceDifference = Math.abs(debitNum - creditNum);

  // --- Fetch database file statuses ---
  const fetchDbStatus = () => {
    const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
    const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

    fetch(`${API_BASE}/api/database/status`, {
      headers: {
        'Authorization': `Bearer ${API_KEY}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.success) {
          setDbStatus(data);
          setIsApiOffline(false);
        }
      })
      .catch(err => {
        console.warn("Cloud status API offline or unauthorized, using local/cached metadata:", err);
        setIsApiOffline(true);
        // Load mock database status for Cloud Mode
        const lastSync = localStorage.getItem('cloud_last_sync') || new Date().toLocaleString('id-ID');
        const syncStatus = localStorage.getItem('cloud_sync_status') || 'SUCCESS';
        setDbStatus({
          success: true,
          mode: 'Cloud (Serverless D1)',
          juaraGadai: {
            exists: true,
            size: 'Cloud Database',
            syncMetadata: {
              last_sync: lastSync,
              status: syncStatus,
            }
          },
          sahabatGadai: {
            exists: true,
            size: 'Cloud Database',
            syncMetadata: {
              last_sync: '17/06/2026, 06:44:00',
              status: 'SUCCESS',
            }
          }
        });
      });
  };

  useEffect(() => {
    fetchDbStatus();

    // Check if local API server is online
    fetch('http://localhost:5001/api/journal/list-adjustments')
      .then(res => {
        if (res.ok) {
          setIsLocalApiOffline(false);
        } else {
          setIsLocalApiOffline(true);
        }
      })
      .catch(() => {
        setIsLocalApiOffline(true);
      });
  }, []);

  // --- Adjustments fetching ---
  const fetchAdjustments = () => {
    setIsListLoading(true);
    setListErrorMessage(null);

    const loadLocalAdjustments = () => {
      const storageKey = activeEntity === 'JUARA_GADAI' ? 'juara_gadai_adjustments' : 'sahabat_gadai_adjustments';
      const localData = localStorage.getItem(storageKey);
      if (localData) {
        try {
          const adjs = JSON.parse(localData);
          setAdjustments(adjs);
        } catch (e) {
          setAdjustments([]);
        }
      } else {
        setAdjustments([]);
      }
      setIsListLoading(false);
    };

    if (isLocalApiOffline) {
      loadLocalAdjustments();
      return;
    }

    fetch('http://localhost:5001/api/journal/list-adjustments')
      .then(res => {
        if (!res.ok) throw new Error('API server offline / failed');
        return res.json();
      })
      .then(data => {
        if (data.success) {
          setAdjustments(data.adjustments);
        } else {
          setListErrorMessage(data.error || 'Gagal memuat jurnal penyesuaian');
        }
        setIsListLoading(false);
      })
      .catch(err => {
        console.error(err);
        // Automatically fallback to local adjustments
        loadLocalAdjustments();
      });
  };

  useEffect(() => {
    if (activeTab === 'list') {
      fetchAdjustments();
    }
  }, [activeTab]);

  // --- Form submission ---
  const handleCreateAdjustment = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSuccessMessage(null);
    setFormErrorMessage(null);

    if (!formAccountCode || !formAccountName) {
      setFormErrorMessage('Silakan pilih akun yang valid!');
      return;
    }

    if (debitNum === 0 && creditNum === 0) {
      setFormErrorMessage('Debit dan Kredit tidak boleh keduanya nol!');
      return;
    }

    setIsSubmittingForm(true);

    const saveAdjustmentLocally = () => {
      const storageKey = activeEntity === 'JUARA_GADAI' ? 'juara_gadai_adjustments' : 'sahabat_gadai_adjustments';
      const localData = localStorage.getItem(storageKey);
      let currentAdjs: Adjustment[] = [];
      if (localData) {
        try {
          currentAdjs = JSON.parse(localData);
        } catch (e) {}
      }

      // Generate a unique journal number: ADJ-YYYYMMDD-XXX
      const prefix = `ADJ-${formDate.replace(/-/g, '')}-`;
      let maxNum = 0;
      currentAdjs.forEach(adj => {
        if (adj.no_journal.startsWith(prefix)) {
          const num = parseInt(adj.no_journal.replace(prefix, '')) || 0;
          if (num > maxNum) maxNum = num;
        }
      });
      const journalNo = `${prefix}${String(maxNum + 1).padStart(3, '0')}`;

      const newAdj: Adjustment = {
        no_journal: journalNo,
        tanggal: formDate,
        no_acc: parseInt(formAccountCode) || 99999,
        name_acc: formAccountName,
        keterangan: formDesc,
        area: formBranch,
        debit: debitNum,
        credit: creditNum,
        amount: debitNum - creditNum
      };

      currentAdjs.push(newAdj);
      localStorage.setItem(storageKey, JSON.stringify(currentAdjs));

      setFormSuccessMessage(`[Mode Cloud] Jurnal penyesuaian ${journalNo} berhasil disimpan di browser & grafik terupdate!`);
      // Clear inputs
      setFormAccountCode('');
      setFormAccountName('');
      setFormDebit('0');
      setFormCredit('0');
      setIsSubmittingForm(false);
      fetchDbStatus();

      // Dispatch event to recalculate Context
      window.dispatchEvent(new Event('financial-data-changed'));
    };

    if (isLocalApiOffline) {
      saveAdjustmentLocally();
      return;
    }

    fetch('http://localhost:5001/api/journal/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tanggal: formDate,
        no_acc: formAccountCode,
        name_acc: formAccountName,
        keterangan: formDesc,
        area: formBranch,
        debit: debitNum,
        credit: creditNum
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setFormSuccessMessage(`Jurnal penyesuaian ${data.journal_no} berhasil dibuat dan disimpan di Excel!`);
          // Clear inputs
          setFormAccountCode('');
          setFormAccountName('');
          setFormDebit('0');
          setFormCredit('0');
          fetchDbStatus();
        } else {
          setFormErrorMessage(data.error || 'Terjadi kesalahan penulisan ke Excel');
        }
        setIsSubmittingForm(false);
      })
      .catch(err => {
        console.error(err);
        // Fallback to local storage
        saveAdjustmentLocally();
      });
  };

  // --- Adjustment Deletion ---
  const handleDeleteAdjustment = (no_journal: string) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus jurnal penyesuaian ${no_journal} secara permanen?`)) {
      return;
    }

    const deleteAdjustmentLocally = () => {
      const storageKey = activeEntity === 'JUARA_GADAI' ? 'juara_gadai_adjustments' : 'sahabat_gadai_adjustments';
      const localData = localStorage.getItem(storageKey);
      if (localData) {
        try {
          const currentAdjs = JSON.parse(localData) as Adjustment[];
          const filtered = currentAdjs.filter(adj => adj.no_journal !== no_journal);
          localStorage.setItem(storageKey, JSON.stringify(filtered));
          alert(`[Mode Cloud] Jurnal ${no_journal} berhasil dihapus dari browser!`);
          fetchAdjustments();
          fetchDbStatus();
          // Dispatch event to recalculate Context
          window.dispatchEvent(new Event('financial-data-changed'));
        } catch (e) {
          alert("Gagal menghapus data dari browser.");
        }
      }
    };

    if (isLocalApiOffline) {
      deleteAdjustmentLocally();
      return;
    }

    fetch('http://localhost:5001/api/journal/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ no_journal })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          alert(`Jurnal ${no_journal} berhasil dihapus dari Excel!`);
          fetchAdjustments();
          fetchDbStatus();
        } else {
          alert(`Gagal menghapus: ${data.error}`);
        }
      })
      .catch(err => {
        console.error(err);
        // Fallback to local delete
        deleteAdjustmentLocally();
      });
  };

  // --- Paste Import Handling ---
  const handleBulkImport = () => {
    setImportSuccess(null);
    setImportError(null);

    if (!pasteText.trim()) {
      setImportError('Silakan tempelkan (paste) baris data terlebih dahulu!');
      return;
    }

    setIsImporting(true);

    const importAdjustmentsLocally = () => {
      const lines = pasteText.trim().split('\n');
      const imported: Adjustment[] = [];
      let skipped = 0;

      lines.forEach((line, index) => {
        if (!line.trim()) return;
        const cols = line.split(pasteText.includes('\t') ? '\t' : ',');
        if (cols.length < 5) {
          skipped++;
          return;
        }

        // Expected cols layout:
        // [Modul, No Journal, Tanggal, mo, Criteria, No acc, Nama Akun, Keterangan, area, Debit, Kredit]
        const mod = cols[0] ? cols[0].trim() : 'ADJ';
        const no_j = cols[1] ? cols[1].trim() : `IMP-${Date.now()}-${index + 1}`;
        const tgl = cols[2] ? cols[2].trim() : new Date().toISOString().substring(0, 10);
        const no_acc = parseInt(cols[5]) || 99999;
        const name_acc = cols[6] ? cols[6].trim() : 'Imported Account';
        const keterangan = cols[7] ? cols[7].trim() : 'Imported Transaction';
        const area = cols[8] ? cols[8].trim() : 'HO';
        const debit = parseFloat(cols[9]) || 0;
        const credit = parseFloat(cols[10]) || 0;

        imported.push({
          no_journal: no_j,
          tanggal: tgl,
          no_acc,
          name_acc,
          keterangan,
          area,
          debit,
          credit,
          amount: debit - credit
        });
      });

      if (imported.length === 0) {
        setImportError(`Gagal mengimpor data. Format kolom tidak sesuai atau tidak ada baris valid (dilewati: ${skipped}).`);
        setIsImporting(false);
        return;
      }

      const storageKey = activeEntity === 'JUARA_GADAI' ? 'juara_gadai_adjustments' : 'sahabat_gadai_adjustments';
      const localData = localStorage.getItem(storageKey);
      let currentAdjs: Adjustment[] = [];
      if (localData) {
        try {
          currentAdjs = JSON.parse(localData);
        } catch (e) {}
      }

      // Avoid duplication of journal no
      const merged = [...currentAdjs];
      imported.forEach(imp => {
        const dupIdx = merged.findIndex(x => x.no_journal === imp.no_journal);
        if (dupIdx !== -1) {
          merged[dupIdx] = imp; // update
        } else {
          merged.push(imp); // append
        }
      });

      localStorage.setItem(storageKey, JSON.stringify(merged));
      setImportSuccess(`[Mode Cloud] Berhasil mengimpor massal ${imported.length} baris transaksi langsung ke browser & memperbarui grafik!`);
      setPasteText('');
      setIsImporting(false);
      fetchDbStatus();

      // Dispatch event to recalculate Context
      window.dispatchEvent(new Event('financial-data-changed'));
    };

    if (isLocalApiOffline) {
      setTimeout(importAdjustmentsLocally, 600); // Small delay to feel premium
      return;
    }

    fetch('http://localhost:5001/api/database/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ csv_text: pasteText })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setImportSuccess(`Berhasil mengimpor massal ${data.count} baris transaksi langsung ke sheet 'GL GAB' file Excel!`);
          setPasteText('');
          fetchDbStatus();
        } else {
          setImportError(data.error || 'Gagal mengimpor data ke Excel.');
        }
        setIsImporting(false);
      })
      .catch(err => {
        console.error(err);
        // Fallback to local import
        importAdjustmentsLocally();
      });
  };

  // --- Google Sheets Synchronization (Actual Cloudflare D1 Sync) ---
  const handleStartSync = () => {
    setIsSyncing(true);
    setSyncResult('idle');
    setSyncProgress({ pct: 0, step: 'connect', detail: 'Memulai sinkronisasi...' });
    setSyncLogs([`[${new Date().toLocaleTimeString()}] Memulai sinkronisasi Google Spreadsheet...`, `ID: ${spreadsheetId}`]);

    const addLog = (msg: string) => {
      setSyncLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
    };

    const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
    const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';

    addLog('Menghubungkan ke API Cloudflare dan memulai unduhan file...');
    setSyncProgress({ pct: 10, step: 'connect', detail: 'Menghubungkan ke Google Sheets...' });

    const authHeaders = { 'Authorization': `Bearer ${API_KEY}` };

    // Step 1: Download in parallel —
    //  (a) the workbook xlsx, parsed for the small pre-aggregated sheets only, and
    //  (b) the GL tab as CSV (the GL tab is far too large to parse from xlsx in-browser
    //      without crashing the tab with Out-of-Memory).
    Promise.all([
      fetch(`${API_BASE}/api/database/download-sheet?spreadsheet_id=${spreadsheetId}`, { headers: authHeaders }),
      fetch(`${API_BASE}/api/database/download-csv?spreadsheet_id=${spreadsheetId}&sheet=GL%20GAB`, { headers: authHeaders }),
    ])
      .then(async ([xlsxRes, csvRes]) => {
        if (!xlsxRes.ok) {
          const errData = await xlsxRes.json().catch(() => ({}));
          throw new Error(errData.error || `Unduh workbook gagal: HTTP ${xlsxRes.status}`);
        }
        if (!csvRes.ok) {
          const errData = await csvRes.json().catch(() => ({}));
          throw new Error(errData.error || `Unduh GL CSV gagal: HTTP ${csvRes.status}`);
        }
        addLog('Unduhan spreadsheet & GL berhasil! Membaca data...');
        setSyncProgress({ pct: 30, step: 'download', detail: 'Mengunduh data spreadsheet & buku besar...' });
        return Promise.all([xlsxRes.arrayBuffer(), csvRes.text()]);
      })
      .then(async ([xlsxBuffer, glCsvText]) => {
        addLog('Mengekstrak dan memproses data client-side (SheetJS + CSV)...');
        setSyncProgress({ pct: 45, step: 'compile', detail: 'Mengekstrak sheets dan mengompilasi data...' });

        // Step 2: Client-side parsing — aggregates from xlsx, GL detail from CSV
        const { compiledData, transactionsBatch } = parseHybridSync(xlsxBuffer, glCsvText, activeEntity);

        addLog(`Kompilasi sukses! Total baris transaksi buku besar: ${transactionsBatch.length}`);
        setSyncProgress({ pct: 55, step: 'compile', detail: 'Menyimpan hasil ringkasan data...' });

        // Step 3: Save financial summary
        const summaryRes = await fetch(`${API_BASE}/api/database/save-summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${API_KEY}`
          },
          body: JSON.stringify({
            entity: activeEntity,
            compiledData,
            transactionsCount: transactionsBatch.length
          })
        });

        if (!summaryRes.ok) {
          const errData = await summaryRes.json().catch(() => ({}));
          throw new Error(errData.error || `Gagal menyimpan ringkasan: HTTP ${summaryRes.status}`);
        }
        addLog('Ringkasan data keuangan berhasil disimpan ke D1!');
        setSyncProgress({ pct: 60, step: 'compile', detail: 'Mengunggah transaksi buku besar...' });

        // Step 4: Batched insert transactions in chunks
        const CHUNK_SIZE = 5000;
        const totalRows = transactionsBatch.length;
        let uploadedRows = 0;
        let chunkIndex = 0;

        while (transactionsBatch.length > 0) {
          // Use splice to shrink array and allow GC of uploaded chunk items
          const chunk = transactionsBatch.splice(0, CHUNK_SIZE);
          const isFirstChunk = (uploadedRows === 0);
          
          addLog(`Mengunggah transaksi buku besar chunk ke-${chunkIndex + 1} (${chunk.length} baris)...`);
          
          const chunkRes = await fetch(`${API_BASE}/api/database/insert-transactions-chunk`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${API_KEY}`
            },
            body: JSON.stringify({
              entity: activeEntity,
              chunk,
              isFirstChunk
            })
          });

          if (!chunkRes.ok) {
            const errData = await chunkRes.json().catch(() => ({}));
            throw new Error(errData.error || `Gagal menyisipkan chunk ${chunkIndex + 1}: HTTP ${chunkRes.status}`);
          }

          uploadedRows += chunk.length;
          chunkIndex++;
          const pct = Math.min(95, Math.round(60 + (uploadedRows / totalRows) * 35));
          setSyncProgress({
            pct,
            step: 'compile',
            detail: `Telah mengunggah ${uploadedRows} dari ${totalRows} baris...`
          });

          // Yield to browser main thread to prevent OOM / freezing and allow garbage collection
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        setIsSyncing(false);
        setSyncProgress({ pct: 100, step: 'done', detail: 'Sinkronisasi selesai!' });
        setSyncResult('success');
        addLog(`✅ SINKRONISASI SUKSES 100%! D1 Database berhasil diperbarui (${totalRows} baris).`);

        const timeStr = new Date().toLocaleString('id-ID');
        localStorage.setItem('cloud_last_sync', timeStr);
        localStorage.setItem('cloud_sync_status', 'SUCCESS');

        // Dispatch custom event to notify DataContext to refresh financial data
        window.dispatchEvent(new CustomEvent('financial-data-changed'));
        fetchDbStatus();
      })
      .catch((err) => {
        console.error(err);
        setIsSyncing(false);
        setSyncResult('error');
        addLog(`❌ ERROR: Gagal sinkronisasi. ${err.message || String(err)}`);

        localStorage.setItem('cloud_sync_status', 'FAILED');
        fetchDbStatus();
      });
  };

  // Group branches by Area
  const branchesByArea = useMemo(() => {
    const groups: Record<string, typeof branches> = { Depok: [], Bogor: [], Bekasi: [] };
    branches.forEach((b) => {
      if (groups[b.area]) groups[b.area].push(b);
    });
    return groups;
  }, []);

  return (
    <div className="data-table-card animate-in" style={{ minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Dynamic Style Sheet for hover micro-animations */}
      <style dangerouslySetInnerHTML={{__html: `
        .db-tab-container {
          display: flex;
          gap: 6px;
          background: rgba(255, 255, 255, 0.015);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 30px;
          padding: 4px;
          width: fit-content;
          margin: 0 auto 24px 0;
          backdrop-filter: blur(10px);
          box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.05), 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .db-tab {
          padding: 8px 18px;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          border: 1px solid transparent;
          background: none;
          outline: none;
          color: var(--text-secondary);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 6px;
          border-radius: 25px;
        }
        .db-tab:hover {
          color: var(--text-primary);
          background-color: rgba(255, 255, 255, 0.03);
          transform: translateY(-1px);
        }
        .db-tab.active {
          color: ${activeEntity === 'JUARA_GADAI' ? '#c4b5fd' : '#a7f3d0'};
          background: ${activeEntity === 'JUARA_GADAI' 
            ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(109, 40, 217, 0.25) 100%)' 
            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(4, 120, 87, 0.25) 100%)'};
          border-color: ${activeEntity === 'JUARA_GADAI' ? 'rgba(139, 92, 246, 0.25)' : 'rgba(16, 185, 129, 0.25)'};
          box-shadow: 0 4px 12px ${activeEntity === 'JUARA_GADAI' ? 'rgba(139, 92, 246, 0.12)' : 'rgba(16, 185, 129, 0.12)'};
          transform: translateY(0);
        }
        .suggestion-item:hover {
          background-color: var(--sidebar-item-hover) !important;
        }
        .btn-sync {
          background: ${activeEntity === 'JUARA_GADAI' 
            ? 'linear-gradient(135deg, #F68B1F 0%, #F47920 100%)' 
            : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'} !important;
          border: none !important;
          color: white !important;
          box-shadow: 0 4px 14px ${activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.4)' : 'rgba(139, 92, 246, 0.4)'} !important;
        }
        .btn-sync:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px ${activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.6)' : 'rgba(139, 92, 246, 0.6)'} !important;
        }
      `}} />

      {/* 1. Dashboard Header */}
      <div className="data-table-header" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '16px' }}>
        <div>
          <div className="data-table-title" style={{ fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🗄️</span> Manajemen Database Excel & Drive
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Profil Aktif: <strong>{activeEntity === 'JUARA_GADAI' ? '🟣 Juara Gadai Group' : '🟢 Sahabat Gadai (JGJB Finance)'}</strong>
          </div>
        </div>

        {/* Database Sync Indicators */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <div className="kpi-sum-card" style={{ padding: '8px 14px', background: 'var(--bg-input)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', fontSize: '11px', textAlign: 'right' }}>
            <span style={{ color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', fontSize: '9px', fontWeight: '700' }}>Excel Database</span>
            <span style={{ fontWeight: '800', color: dbStatus?.sahabatGadai?.exists ? 'var(--emerald-light)' : 'var(--red-light)' }}>
              {dbStatus?.sahabatGadai?.exists ? `Aktif (${dbStatus.sahabatGadai.sizeMB} MB)` : 'Off / Belum Terbaca'}
            </span>
          </div>
          {activeEntity === 'JUARA_GADAI' && (
            <div className="kpi-sum-card" style={{ padding: '8px 14px', background: 'var(--bg-input)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', fontSize: '11px', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '170px' }}>
              <span style={{ color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase', fontSize: '9px', fontWeight: '700' }}>Google Sheet Cache</span>
              <span style={{ fontWeight: '800', color: dbStatus?.juaraGadai?.exists ? '#a78bfa' : 'var(--red-light)' }}>
                {dbStatus?.juaraGadai?.exists ? `Tersinkron (${dbStatus.juaraGadai.sizeMB} MB)` : 'Belum Sinkron'}
              </span>
              {dbStatus?.juaraGadai?.syncMetadata ? (
                <div style={{ fontSize: '9px', marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', opacity: 0.9 }}>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    backgroundColor: dbStatus.juaraGadai.syncMetadata.status === 'SUCCESS' ? '#10b981' : '#ef4444',
                    boxShadow: dbStatus.juaraGadai.syncMetadata.status === 'SUCCESS' ? '0 0 6px #10b981' : '0 0 6px #ef4444'
                  }} />
                  <span style={{ color: dbStatus.juaraGadai.syncMetadata.status === 'SUCCESS' ? '#a7f3d0' : '#fca5a5', fontWeight: '700', fontSize: '8.5px' }}>
                    {dbStatus.juaraGadai.syncMetadata.status === 'SUCCESS' ? 'SUCCESS' : 'FAILED'}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '8.5px', fontFamily: 'monospace' }}>
                    {dbStatus.juaraGadai.syncMetadata.last_sync}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: '8.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Belum pernah sync
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 2. Sub-navigation tabs */}
      <div className="db-tab-container">
        <button className={`db-tab ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>
          📋 Daftar Penyesuaian
        </button>
        <button className={`db-tab ${activeTab === 'import' ? 'active' : ''}`} onClick={() => setActiveTab('import')}>
          📥 Impor Paste Massal
        </button>
        <button className={`db-tab ${activeTab === 'sync' ? 'active' : ''}`} onClick={() => setActiveTab('sync')}>
          🔄 Google Sheets Sync
        </button>
      </div>

      {/* Tab content area */}
      <div style={{ flex: 1 }}>
        {isLocalApiOffline ? (
          <div style={{
            padding: '16px 20px',
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            border: '1.5px solid rgba(139, 92, 246, 0.35)',
            borderRadius: 'var(--radius-lg)',
            color: '#c4b5fd',
            fontSize: '13.5px',
            lineHeight: '1.6',
            marginBottom: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            boxShadow: '0 4px 20px rgba(139, 92, 246, 0.15)',
            backdropFilter: 'blur(10px)',
            animation: 'fadeIn 0.5s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', fontSize: '15px', color: '#a78bfa' }}>
              <span>📴</span> Mode Offline — API Lokal Tidak Aktif
            </div>
            <div>
              Server API lokal (port 5001) tidak terhubung. Perubahan <strong>data jurnal</strong> hanya tersimpan sementara di browser ini dan <strong>TIDAK</strong> tertulis ke file Excel master. Data <strong>budget</strong> tetap aman tersinkron ke Cloudflare D1 (cloud database).
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', borderTop: '1px solid rgba(139, 92, 246, 0.15)', paddingTop: '8px', marginTop: '4px' }}>
              ℹ️ Untuk menulis adjustment langsung ke Excel, jalankan <code>npm run api:server</code> di terminal komputer Anda.
            </div>
          </div>
        ) : (
          <div style={{
            padding: '12px 18px',
            backgroundColor: 'rgba(16, 185, 129, 0.08)',
            border: '1.5px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 'var(--radius-lg)',
            color: '#a7f3d0',
            fontSize: '13px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.05)',
            animation: 'fadeIn 0.5s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800' }}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 8px #10b981', animation: 'pulse 1s infinite alternate' }} />
              <span>Koneksi Server Lokal Aktif (Port 5001)</span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Menulis langsung ke file Excel di komputer Anda
            </div>
          </div>
        )}



        {/* --- TAB 2: DAFTAR JURNAL PENYESUAIAN --- */}
        {activeTab === 'list' && (
          <div className="animate-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  Daftar Jurnal Penyesuaian
                </h3>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Menampilkan semua penyesuaian lokal (`Modul == 'ADJ'`) yang tersimpan di Excel.
                </p>
              </div>
              
              <button onClick={fetchAdjustments} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                🔄 Segarkan
              </button>
            </div>

            {isListLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <div style={{ margin: '0 auto 16px', width: '32px', height: '32px', borderRadius: '50%', border: '3px solid var(--border-light)', borderTopColor: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : 'var(--emerald)', animation: 'spin 1s linear infinite' }} />
                <div>Memuat daftar penyesuaian...</div>
              </div>
            ) : listErrorMessage ? (
              <div style={{ padding: '24px', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--red)', borderRadius: 'var(--radius-md)', color: 'var(--red-light)', fontSize: '13px' }}>
                <strong>Error:</strong> {listErrorMessage}
              </div>
            ) : adjustments.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', border: '2px dashed var(--border-light)', borderRadius: 'var(--radius-lg)' }}>
                <span style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}>📁</span>
                <div style={{ fontWeight: '700', color: 'var(--text-primary)' }}>Tidak Ada Jurnal Penyesuaian</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Belum ada transaksi penyesuaian lokal (`Modul == 'ADJ'`) yang dicatat di file Excel.
                </div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>No Journal</th>
                      <th>Tanggal</th>
                      <th>Akun</th>
                      <th>Keterangan</th>
                      <th>Area</th>
                      <th style={{ textAlign: 'right' }}>Debit</th>
                      <th style={{ textAlign: 'right' }}>Kredit</th>
                      <th style={{ textAlign: 'right' }}>Net</th>
                      <th style={{ textAlign: 'center' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustments.map((adj, index) => (
                      <tr key={index}>
                        <td style={{ fontWeight: '700', fontSize: '12px', fontFamily: 'monospace' }}>{adj.no_journal}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{adj.tanggal}</td>
                        <td>
                          <div style={{ fontWeight: '600' }}>{adj.name_acc}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{adj.no_acc}</div>
                        </td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={adj.keterangan}>
                          {adj.keterangan}
                        </td>
                        <td style={{ fontWeight: '600', fontFamily: 'monospace' }}>{adj.area}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{adj.debit > 0 ? formatRupiahFull(adj.debit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{adj.credit > 0 ? formatRupiahFull(adj.credit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: '800', color: adj.amount >= 0 ? 'var(--emerald-light)' : 'var(--red-light)' }}>
                          {formatRupiahFull(adj.amount)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteAdjustment(adj.no_journal)}
                            style={{
                              border: 'none',
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: 'var(--red-light)',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '11px',
                            }}
                          >
                            🗑️ Hapus
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* --- TAB 3: IMPOR PASTE MASSAL --- */}
        {activeTab === 'import' && (
          <div className="animate-in" style={{ maxWidth: '780px', margin: '0 auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-primary)' }}>
              Impor Massal Paste dari Excel
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
              Salin (Copy) tabel transaksi dari file Excel kerja Anda, lalu tempelkan (Paste) langsung ke kotak di bawah untuk mengimpor massal ke database `GL GAB` Excel.
            </p>

            {importSuccess && (
              <div style={{ padding: '12px 16px', backgroundColor: 'rgba(16, 185, 129, 0.12)', border: '1px solid var(--emerald)', borderRadius: 'var(--radius-md)', color: 'var(--emerald-light)', fontSize: '13px', marginBottom: '20px' }}>
                🎉 {importSuccess}
              </div>
            )}

            {importError && (
              <div style={{ padding: '12px 16px', backgroundColor: 'rgba(239, 68, 68, 0.12)', border: '1px solid var(--red)', borderRadius: 'var(--radius-md)', color: 'var(--red-light)', fontSize: '13px', marginBottom: '20px' }}>
                ⚠️ <strong>Gagal Impor:</strong> {importError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.5', padding: '10px 14px', backgroundColor: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)' }}>
                <strong>Petunjuk Format Paste:</strong><br />
                Tempelkan baris data dengan format kolom default (Pemisah Tab):<br />
                <code>Modul [Tab] No Journal [Tab] Tanggal (YYYY-MM-DD) [Tab] mo [Tab] Criteria [Tab] No Acc [Tab] Nama Akun [Tab] Keterangan [Tab] Cabang [Tab] Debit [Tab] Kredit</code><br />
                <em>*Jika kolom parsial, kolom minimal adalah: Tanggal, No Acc, Nama Akun, Keterangan, Cabang, Debit, Kredit. Kolom lainnya akan dihasilkan otomatis.</em>
              </div>

              <textarea
                rows={12}
                placeholder="Tempel baris data Excel di sini... (Contoh: ADJ  ADJ-2026-001  2026-05-23  2026-05-01  BS  11101  Kas  Penyesuaian  HO  500000  0)"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-input)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />

              <button
                onClick={handleBulkImport}
                disabled={isImporting || !pasteText.trim()}
                className="btn btn-primary"
                style={{
                  height: '42px',
                  fontWeight: '700',
                  justifyContent: 'center',
                  backgroundColor: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : 'var(--emerald)',
                  borderColor: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : 'var(--emerald)',
                }}
              >
                {isImporting ? 'Memproses & Menulis ke Excel...' : '🚀 Verifikasi & Impor Jurnal ke Excel'}
              </button>
            </div>
          </div>
        )}

        {/* --- TAB 4: GOOGLE SHEETS DRIVE SYNC --- */}
        {activeTab === 'sync' && (
          <div className="animate-in" style={{ maxWidth: '780px', margin: '0 auto' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-primary)' }}>
              Google Sheets Drive Synchronization
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
              Sinkronisasikan database profil **Juara Gadai Group** langsung dari file Google Spreadsheet Drive secara live.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Linked GSheet info card */}
              <div 
                style={{ 
                  padding: '16px', 
                  backgroundColor: 'rgba(246, 139, 31, 0.05)', 
                  border: '1px solid rgba(246, 139, 31, 0.2)', 
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: '800', color: '#F68B1F', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🔗</span> Google Spreadsheet Tertaut
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                  <a 
                    href="https://docs.google.com/spreadsheets/d/1jVEh7BvrFYEIKwBjv2UskrpkMWMs5pTWVHcgk7ogM_s/edit?gid=473719093#gid=473719093" 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ color: '#F68B1F', textDecoration: 'underline' }}
                  >
                    FS Consol JGJB JGK - Google Sheets
                  </a>
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  *Pastikan Spreadsheet di-share ke <strong>"Anyone with link can view"</strong> agar backend sinkronisasi dapat mengunduh database secara instan!
                </div>
              </div>

              {/* ID Input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Google Spreadsheet ID</label>
                <input
                  type="text"
                  placeholder="ID Google Sheet..."
                  value={spreadsheetId}
                  onChange={(e) => setSpreadsheetId(e.target.value)}
                  style={{
                    padding: '9px 12px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-light)',
                    backgroundColor: 'var(--bg-input)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Sync Trigger button */}
              <button
                onClick={handleStartSync}
                disabled={isSyncing}
                className="btn btn-sync"
                style={{
                  height: '44px',
                  fontWeight: '800',
                  fontSize: '14px',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                {isSyncing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }} />
                    <span>Sinkronisasi Google Drive Berjalan...</span>
                  </div>
                ) : (
                  <span>⚡ Tarik & Sinkronkan Data dari Google Sheets</span>
                )}
              </button>

              {/* ===== PROGRESS BAR & TERMINAL ===== */}
              {(syncLogs.length > 0 || isSyncing) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>

                  {/* Step Indicators */}
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {[
                      { key: 'connect', icon: '🔗', label: 'Koneksi' },
                      { key: 'download', icon: '📥', label: 'Unduhan' },
                      { key: 'compile', icon: '⚙️', label: 'Kompilasi' },
                      { key: 'done', icon: '✅', label: 'Selesai' },
                    ].map((s, i, arr) => {
                      const stepOrder = ['connect', 'download', 'verify', 'compile', 'compile_ts', 'compile_gl', 'done'];
                      const currentIdx = stepOrder.indexOf(syncProgress.step);
                      const thisIdx = stepOrder.indexOf(s.key);
                      const isActive = syncProgress.step === s.key || 
                        (s.key === 'download' && syncProgress.step === 'verify') ||
                        (s.key === 'compile' && (syncProgress.step === 'compile_ts' || syncProgress.step === 'compile_gl'));
                      const isPast = thisIdx < currentIdx && thisIdx >= 0;
                      const chipColor = isActive ? (activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#8b5cf6') : isPast ? '#22c55e' : 'var(--border-light)';
                      const textColor = isActive ? (activeEntity === 'JUARA_GADAI' ? '#FBA94D' : '#c4b5fd') : isPast ? '#86efac' : 'var(--text-muted)';

                      return (
                        <React.Fragment key={s.key}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '5px 10px',
                              borderRadius: '20px',
                              border: `1.5px solid ${chipColor}`,
                              backgroundColor: isActive ? (activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.12)' : 'rgba(139,92,246,0.12)') : isPast ? 'rgba(34,197,94,0.08)' : 'transparent',
                              transition: 'all 0.4s ease',
                              fontSize: '11px',
                              fontWeight: isActive ? '700' : '600',
                              color: textColor,
                              whiteSpace: 'nowrap' as const,
                            }}
                          >
                            <span style={{ fontSize: '13px' }}>{isPast ? '✓' : s.icon}</span>
                            {s.label}
                            {isActive && isSyncing && (
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#a78bfa', animation: 'pulse 1s infinite alternate', marginLeft: '2px' }} />
                            )}
                          </div>
                          {i < arr.length - 1 && (
                            <div style={{
                              width: '20px',
                              height: '2px',
                              backgroundColor: isPast ? '#22c55e' : 'var(--border-light)',
                              transition: 'background-color 0.4s ease',
                            }} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Progress Bar */}
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px',
                    }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: syncResult === 'success' ? '#22c55e' : syncResult === 'error' ? '#ef4444' : (activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#a78bfa'),
                      }}>
                        {syncResult === 'success' ? '✅ Sinkronisasi Berhasil!' : syncResult === 'error' ? '❌ Gagal' : syncProgress.detail || 'Memulai...'}
                      </span>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '800',
                        fontFamily: 'monospace',
                        color: syncProgress.pct >= 100 ? '#22c55e' : (activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#c4b5fd'),
                      }}>
                        {syncProgress.pct}%
                      </span>
                    </div>
                    {/* Bar track */}
                    <div style={{
                      width: '100%',
                      height: '10px',
                      borderRadius: '8px',
                      backgroundColor: activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid ' + (activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.15)' : 'rgba(139, 92, 246, 0.15)'),
                      overflow: 'hidden',
                      position: 'relative',
                    }}>
                      {/* Bar fill */}
                      <div style={{
                        width: `${syncProgress.pct}%`,
                        height: '100%',
                        borderRadius: '8px',
                        background: syncResult === 'error'
                          ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                          : syncProgress.pct >= 100
                            ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                            : activeEntity === 'JUARA_GADAI'
                              ? 'linear-gradient(90deg, #F68B1F, #FBA94D, #F68B1F)'
                              : 'linear-gradient(90deg, #8b5cf6, #a78bfa, #c4b5fd, #a78bfa, #8b5cf6)',
                        backgroundSize: syncProgress.pct < 100 ? '200% 100%' : '100% 100%',
                        animation: syncProgress.pct < 100 && isSyncing ? 'shimmer 2s linear infinite' : 'none',
                        transition: 'width 0.5s ease-out',
                        boxShadow: syncProgress.pct > 0 && syncProgress.pct < 100
                          ? activeEntity === 'JUARA_GADAI'
                            ? '0 0 12px rgba(246, 139, 31, 0.5), 0 0 4px rgba(246, 139, 31, 0.3)'
                            : '0 0 12px rgba(139, 92, 246, 0.5), 0 0 4px rgba(139, 92, 246, 0.3)'
                          : syncProgress.pct >= 100
                            ? '0 0 12px rgba(34, 197, 94, 0.5)'
                            : 'none',
                      }} />
                    </div>
                  </div>

                  {/* Success result card */}
                  {syncResult === 'success' && (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: 'rgba(34, 197, 94, 0.08)',
                      border: '1px solid rgba(34, 197, 94, 0.25)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}>
                      <span style={{ fontSize: '22px' }}>🎉</span>
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '13px', color: '#86efac' }}>
                          Data Berhasil Disinkronkan!
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          Database Cloudflare D1 telah berhasil diperbarui dengan data terbaru. Silakan refresh halaman untuk memuat data terbaru.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Terminal log */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px',
                      }}
                    >
                      <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>
                        Terminal Log
                      </span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                        {syncLogs.length} entri
                      </span>
                    </div>
                    <div
                      ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}
                      style={{
                        backgroundColor: '#090d16',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-md)',
                        padding: '14px 16px',
                        fontFamily: 'monospace',
                        fontSize: '10.5px',
                        color: '#94a3b8',
                        maxHeight: '180px',
                        overflowY: 'auto',
                        lineHeight: '1.7',
                        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8)'
                      }}
                    >
                      {syncLogs.map((log, index) => (
                        <div
                          key={index}
                          style={{
                            borderBottom: '1px solid rgba(255,255,255,0.02)',
                            padding: '2px 0',
                            color: log.includes('ERROR') || log.includes('❌') ? '#f87171' :
                                   log.includes('✅') || log.includes('✓') || log.includes('SUKSES') ? '#86efac' :
                                   log.includes('Mengunduh') ? '#93c5fd' : '#94a3b8'
                          }}
                        >
                          {log}
                        </div>
                      ))}
                      {isSyncing && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', color: '#60a5fa' }}>
                          <div style={{ width: '6px', height: '6px', backgroundColor: '#60a5fa', borderRadius: '50%', animation: 'pulse 1s infinite alternate' }} />
                          <span>Proses berjalan...</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <style dangerouslySetInnerHTML={{__html: `
                    @keyframes pulse { from { opacity: 0.3; } to { opacity: 1; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                  `}} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabasePage;
